import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Line, Path, Stop } from 'react-native-svg';
import type { V2RateHistoryPoint } from '../../types/v2';
import { buildChartGeometry, parseHistoryPrices } from '../../utils/marketChart';
import { COLORS } from '../../theme/themeTokens';

interface MarketChartProps {
  points: V2RateHistoryPoint[];
  height?: number;
  compact?: boolean;
  color?: string;
  emptyLabel?: string;
}

let chartSequence = 0;

const MarketChart: React.FC<MarketChartProps> = ({
  points,
  height = 210,
  compact = false,
  color = COLORS.mint,
  emptyLabel = 'Market data unavailable',
}) => {
  const [width, setWidth] = useState(0);
  const gradientId = useMemo(() => `market-chart-${chartSequence++}`, []);
  const prices = useMemo(() => parseHistoryPrices(points), [points]);
  const plotWidth = compact ? width : Math.max(0, width - 54);
  const geometry = useMemo(
    () => buildChartGeometry(prices, plotWidth, height, compact ? 2 : 12),
    [height, plotWidth, prices, compact],
  );

  return (
    <View
      style={[styles.container, compact && styles.compactContainer, { height }]}
      onLayout={event => setWidth(event.nativeEvent.layout.width)}
    >
      {width > 0 && geometry ? (
        <Svg width={width} height={height}>
          <Defs>
            <LinearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <Stop offset="0" stopColor={color} stopOpacity={compact ? 0.42 : 0.32} />
              <Stop offset="1" stopColor={color} stopOpacity="0.02" />
            </LinearGradient>
          </Defs>
          {!compact && [0, 1, 2, 3, 4].map(index => {
            const y = 12 + index * ((height - 24) / 4);
            return <Line key={index} x1="0" y1={y} x2={plotWidth} y2={y} stroke={COLORS.softGrey} strokeWidth="1" />;
          })}
          <Path d={geometry.areaPath} fill={`url(#${gradientId})`} />
          <Path d={geometry.linePath} fill="none" stroke={color} strokeWidth={compact ? 1.5 : 2} strokeLinecap="round" />
          {!compact && [0, 1, 2, 3, 4].map(index => {
            const y = 16 + index * ((height - 24) / 4);
            return (
              <React.Fragment key={`label-${index}`}>
                <Line x1={plotWidth + 5} y1={y - 4} x2={plotWidth + 8} y2={y - 4} stroke={COLORS.blueSteel} />
              </React.Fragment>
            );
          })}
        </Svg>
      ) : prices.length === 0 ? (
        <View style={styles.empty}><Text style={styles.emptyText}>{emptyLabel}</Text></View>
      ) : null}
      {!compact && geometry && (
        <View pointerEvents="none" style={styles.axisLabels}>
          {[0, 1, 2, 3, 4].map(index => {
            const value = geometry.max - index * ((geometry.max - geometry.min) / 4);
            return <Text key={index} style={styles.axisText}>{formatAxisPrice(value)}</Text>;
          })}
        </View>
      )}
    </View>
  );
};

const formatAxisPrice = (value: number) => {
  if (value >= 1000) { return Math.round(value).toLocaleString('en-US').replace(/,/g, ' '); }
  if (value >= 1) { return value.toFixed(2); }
  return value.toPrecision(3);
};

const styles = StyleSheet.create({
  container: { width: '100%', position: 'relative' },
  compactContainer: { minWidth: 56 },
  empty: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
  emptyText: { color: COLORS.blueSteel, fontSize: 13 },
  axisLabels: { position: 'absolute', right: 0, top: 5, bottom: 5, justifyContent: 'space-between', width: 48 },
  axisText: { color: COLORS.blueSteel, fontSize: 9, textAlign: 'right' },
});

export default MarketChart;
